import { useEffect, useRef, useState } from "react";
import { STATS_SECTION } from "../../data/siteData";
import "./Stats.css";

/* ============================================================
   COUNT UP
   ============================================================ */

function useCountUp(target, shouldStart, duration = 1400) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!shouldStart) return;

    let raf;
    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min(
        (now - start) / duration,
        1
      );

      const eased =
        1 - (1 - progress) * (1 - progress);

      setValue(Math.floor(eased * target));

      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      }
    };

    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
    };
  }, [shouldStart, target, duration]);

  return value;
}

/* ============================================================
   STAT ITEM
   ============================================================ */

function StatItem({ stat, shouldStart }) {
  const value = useCountUp(
    stat.value,
    shouldStart
  );

  return (
    <div className="stats__item">
      <span className="stats__value">
        {value.toLocaleString()}
        {stat.suffix}
      </span>

      <span className="stats__label">
        {stat.label}
      </span>
    </div>
  );
}

/* ============================================================
   STATS SECTION
   ============================================================ */

function Stats() {
  const sectionRef = useRef(null);

  const [inView, setInView] = useState(false);

  /* ==========================================================
     INTERSECTION OBSERVER
     ========================================================== */

  useEffect(() => {
    const element = sectionRef.current;

    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);

          observer.disconnect();
        }
      },
      {
        threshold: 0.3,
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="stats"
    >
      <div className="container">

        {/* ==================================================
            HEADING
            ================================================== */}

        <h2 className="stats__heading">
          {STATS_SECTION.heading}
        </h2>

        {/* ==================================================
            STATS
            ================================================== */}

        <div className="stats__grid">

          {STATS_SECTION.stats.map((stat) => (
            <StatItem
              key={stat.label}
              stat={stat}
              shouldStart={inView}
            />
          ))}

        </div>

      </div>
    </section>
  );
}

export default Stats;